import type { UserRole, UserTier } from "../schemas/enums";

// ---------------------------------------------------------------------------
// plPool — PL-Eligibility (Phase 5.59)
// ---------------------------------------------------------------------------
// Single source of truth fuer "darf dieser User Projektleiter (PL) sein?".
// Wird gleichzeitig von Backend (grok-assign-projectlead-Cron, Self-Test) und
// Frontend (ProjectOwnerPicker, ProjectsListPage 5.45-Tile-Reihe) konsumiert,
// damit der angebotene Pool an allen Stellen identisch ist.
//
// Daniel-Klarstellung 2026-05-17 (Plan-Chat-Sync, HANDOVER):
//   - PL-Pool = {Corsin, Marvin, Sandro mit CEO+PL}.
//   - Daniel ist superadmin mit `tier=null` und NIE PL — sein Job ist
//     OS-Pflege, nicht Projekt-Fuehrung.
//   - Jacqueline ist buchhaltung mit `tier='Fixlohn'` und NIE PL —
//     Buchhaltung leitet keine Projekte.
//
// Eligibility-Regel (idempotent, pur, ohne DB-Zugriff):
//   - role ∈ {'pl', 'admin'}          — superadmin + buchhaltung raus.
//   - tier !== null                   — Onboarding noch nicht abgeschlossen raus.
//   - tier !== 'Fixlohn'              — kein variabler Anteil → kein PL-Pool.
//
// Honest Pushback: das ist NICHT „aktiv?" — ein offboardeter User mit
// active=false aber tier!=null wuerde hier durchrutschen. Das ist by-design:
// die Eligibility ist eine Rollen-/Tier-Regel, der Caller (z.B. UserRepo.
// listAssignablePls + Frontend useUsers) filtert active=true zusaetzlich.
// Defense in Depth — die zwei Layer haben verschiedene Verantwortlichkeiten.

// `tier` ist absichtlich auf `string | null` gehalten — die DB-Spalte
// `users.tier` ist `text` (kein PG-Enum), Drizzle liefert `string | null`.
// Die `Fixlohn`-Pruefung ist ein simpler String-Vergleich, die zod-strikte
// Enum-Validierung passiert beim Schreiben (Migration 0001/UserService).
export interface PlPoolUserShape {
  readonly role: UserRole;
  readonly tier: string | null;
}

export function isPlEligible(user: PlPoolUserShape): boolean {
  if (user.role !== "pl" && user.role !== "admin") return false;
  if (user.tier === null) return false;
  if (user.tier === "Fixlohn") return false;
  return true;
}

/**
 * Die Tuer des System-Schreibwegs fuer den Ansprechpartner (LEAD-20): aktiv
 * und PL-faehig. Der Schreibweg und der Hinweis am Projekt (TRANSPARENZ-4,
 * «gehört nicht zum Kreis der Projektleiter») fragen beide hier.
 */
export function kannAnsprechpartnerSein(
  user: (PlPoolUserShape & { readonly active: boolean }) | null | undefined,
): boolean {
  return user !== null && user !== undefined && user.active && isPlEligible(user);
}

/**
 * LEAD-15 (Daniel 2026-09-03): das, was der Dialog «PL zuweisen» ueber einen
 * Kandidaten wissen darf — und NICHT mehr. Kommt aus `app.pl_pool()`
 * (Migration 0157, ueber `GET /api/users/pl-pool`), nie aus der `users`-
 * Zeile: die traegt base_lohn, sozial, target und phone, und ein
 * Spalten-GRANT verengt keinen Tabellen-GRANT.
 */
export interface PlPoolMember extends PlPoolUserShape {
  readonly id: string;
  readonly fullName: string;
  readonly tier: UserTier | null;
}
